import React, { createContext, useCallback, useContext, useState } from 'react';

import { getMeta } from '../../api';
import { useFetch } from '../../hooks';
import { useAuth } from '../../shared';

type Cleanup = () => void;

type AppContextType = {
  loading: boolean;
  meta: AppMetaType;

  title: React.ReactNode | null;
  removeTitle: (id: string) => void;
  addTitle: (node: React.ReactNode) => Cleanup;

  refresh: () => void;
};

type TitleEntry = { id: string; node: React.ReactNode };

const AppContext = createContext<AppContextType | undefined>(undefined);

const defaultMeta: AppMetaType = { campaigns: [] };

export const AppProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const { user } = useAuth();

  const [titles, setTitles] = useState<TitleEntry[]>([]);
  const { loading, data, refresh } = useFetch<AppMetaType>(
    user ? getMeta : () => defaultMeta,
    [user]
  );

  const removeTitle = useCallback((id: string) => {
    setTitles(prev => prev.filter(entry => entry.id !== id));
  }, []);

  const addTitle = useCallback(
    (node: React.ReactNode) => {
      const id = crypto.randomUUID();
      setTitles(prev => [...prev, { id, node }]);
      return () => removeTitle(id);
    },
    [removeTitle]
  );

  const contextValue = React.useMemo<AppContextType>(
    () => ({
      loading,

      refresh,
      addTitle,
      removeTitle,

      meta: data || defaultMeta,
      title: titles.at(-1)?.node || null,
    }),
    [loading, data, titles, addTitle, removeTitle, refresh]
  );

  return (
    <AppContext.Provider value={contextValue}>{children}</AppContext.Provider>
  );
};

export const useAppContext = () => {
  const context = useContext(AppContext);
  if (!context) {
    throw new Error('useAppContext must be used within an AppProvider');
  }
  return context;
};
